import * as THREE from 'three';
import { instancedAt, type LandmarkDef } from './common';
import { landmarkModel, windowGlass } from './model';

/**
 * landmarks/napoli.ts — Napoli's landmarks. All hand-built low-poly Blender models
 * (glTF) except vesuvio, which keeps the volcano model plus the instanced
 * town-lights strung across the bay at its foot. Galleria is a drive-through
 * ('span') that scales by width to clear the road.
 */
/**
 * VESUVIUS METRICS — MEASURED off the GLB's accessor bounds (not eyeballed), so
 * every number below is derived from the model itself:
 *   native bbox y −9..79 (88 tall), fitted to `height` 70 → scale 70/88.
 *   'V_green' (the vegetated apron) spans native y −9..+9 = the model's whole
 *   lowest 18 units; 'V_slope_tufa' runs −9..33; the crater sits at 33..36.
 *   Native bbox x −76..62 → landmarkModel's x/z centring shifts the cone axis
 *   (native x 0, confirmed by the crater/lavaglow bounds) to local x +7·scale.
 *
 * THE FIX (graphics overhaul, QA: vesuvio-orbit — a bright green disc floating
 * on the bay at the volcano's foot). The model is TWO STACKED cones, not one:
 * 'V_green' is the wide vegetated apron from native y −9 to +9, and
 * 'V_slope_tufa' is the grey cone sitting ON it from y +9 to 33 (proven from
 * the mesh: the tufa has NO front-sector geometry below y +9 — hiding the green
 * therefore opens the cone's base and hangs it in mid-air, which is exactly
 * what the first attempt did). So the apron has to be SUNK, not hidden.
 *
 * The datum is the other half of it. buildLandmark parks a 'side' landmark at
 * the ROAD's y, which is NOT the bay's surface: water.ts levels each run at the
 * MEDIAN smoothed ground over the whole bayfront. Measured across 10 seeds the
 * landmark origin sits anywhere from 1.93 u BELOW to 4.32 u ABOVE the local
 * surface (tour seed −0.72, seed 123 +4.32 — an elevated stretch of road), so a
 * sink tuned on one seed leaves green proud on another. The sink below clears
 * the apron's full height PLUS that worst case, and costs ~1/4 of the
 * mountain's apparent height in exchange for meeting the sea on every seed.
 *
 * The sink is applied to an INNER group: buildLandmark overwrites the returned
 * group's position, and landmarkModel rewrites its own position when the glTF
 * lands — an intermediate group is the only spot that survives both.
 */
const V_SCALE = 70 / 88;
/** Apron top above the model's base: native y −9..+9, scaled. */
const V_GREEN_TOP = 18 * V_SCALE; // 14.32
/** Apron height + the worst measured origin-above-water (4.32) + headroom. */
const V_SINK = V_GREEN_TOP + 5.2; // 19.52
const V_AXIS_X = 7 * V_SCALE; // 5.57 — the cone axis after x/z centring
/** The tufa cone is a straight frustum, MEASURED off the mesh's two rings:
 *  world radius 33.41 at model-local y 14.32, 12.73 at the crater (33.41). */
const V_CONE_R0 = 33.41;
const V_CONE_Y0 = 14.32;
const V_CONE_SLOPE = (12.73 - 33.41) / (33.41 - 14.32); // −1.083 u of radius per u of height
/** Surface radius at a height `above` the landmark origin (i.e. above the bay
 *  on a typical seed), accounting for the sink. */
function coneRadiusAt(above: number): number {
  return V_CONE_R0 + V_CONE_SLOPE * (V_SINK + above - V_CONE_Y0);
}

function vesuvio(): THREE.Group {
  const g = new THREE.Group();
  // Hand-built low-poly Blender volcano (twin-peaked Vesuvius + Monte Somma, smoking).
  const sunk = new THREE.Group();
  sunk.position.y = -V_SINK;
  sunk.add(landmarkModel('models/landmarks/vesuvius.glb', { height: 70 }));
  g.add(sunk);
  // The little towns strung along the mountain's foot — warm window lights.
  // They used to ring the landmark at radius 78..94, well beyond the model's
  // 60 u reach, so every hamlet floated on the open bay (QA). They now perch on
  // the tufa flank, and BOTH of their coordinates are derived:
  //  - HEIGHT 3..6 above the landmark origin clears the water on every measured
  //    seed (the surface never rises more than 1.93 u above that origin).
  //  - RADIUS comes from the measured frustum at that height (coneRadiusAt),
  //    +0.6 outboard so the slope never swallows a hamlet whole.
  //  - The arc stays inside ±0.75 rad of the ROAD-FACING front (local +Z is the
  //    model's front), so no hamlet wanders round onto the Monte Somma spur,
  //    which reaches local x −55.
  const lights: [number, number, number][] = [];
  for (let i = 0; i < 26; i++) {
    const a = -0.75 + (i / 25) * 1.5;
    const t = ((i * 7) % 5) / 4; // which contour this hamlet clings to
    const y = 3.0 + t * 3.0;
    const r = coneRadiusAt(y) + 0.6;
    lights.push([V_AXIS_X + Math.sin(a) * r, y, Math.cos(a) * r]);
  }
  g.add(instancedAt(new THREE.BoxGeometry(2.6, 1.8, 1.2), 0xffd9a0, lights));
  return g;
}

export const NAPOLI_LANDMARKS: Record<string, LandmarkDef> = {
  vesuvio: { build: vesuvio, placement: 'side', offset: 120 },
  // GS6: noon city — both castles' 'win' slabs burned blown-white in full sun
  // (QA); windowGlass turns them into dark arrow-slit glass with a whisper of
  // warmth. The floating merlon rows are the Napoli Blender round's geometry
  // fix, not a def-level one.
  castelDellOvo: { build: () => landmarkModel('models/landmarks/castel_dell_ovo.glb', { height: 19, ...windowGlass('win', 0.45) }), placement: 'side', offset: 30 },
  castelNuovo: { build: () => landmarkModel('models/landmarks/castel_nuovo.glb', { height: 22, ...windowGlass('win', 0.45) }), placement: 'side', offset: 12 },
  // 'gold' is the dome lantern (the model's ONLY gold mesh — measured off the
  // GLB: x ±2.2, y 21..24). It survived the GS6 emissive-discipline pass as the
  // one blown white-gold glowing cone in the city (QA: plebiscito-orbit) because
  // 0.75 is barely a cut — the material carries emissive (0.90,0.72,0.31) at
  // intensity 1 on top of a metalness-0.8 surface that ALSO mirrors the noon
  // sky, so the two brights sum past white. The fix is all three levers, not
  // one: emissive down to a whisper (a gilded finial should catch light, not
  // emit it), the remaining glow warmed to amber so what does show reads GOLD
  // instead of white, and the base deepened from #d1a84d to a richer gilt so
  // the sky reflection lands on a darker albedo. Napoli is a noon city — the
  // lantern reads by its metal reflection now, which is what real gilding does.
  plebiscito: {
    build: () =>
      landmarkModel('models/landmarks/plebiscito.glb', {
        height: 22,
        emissiveScaleMats: { gold: 0.1 },
        emissiveTintMats: { gold: 0xffb45a },
        colorMats: { gold: 0xb8832a },
      }),
    placement: 'side',
    offset: 14,
  },
  // GS-Napoli EXTENSION ROUND. The previous model was a real glazed hall but only
  // ONE BAY DEEP (measured: 42.7 native along the course, 12 of it the entrance
  // facade), so the chase camera at the finish framed road + arcaded walls + a
  // clipped rib fan and SKY. The vault was never overhead. The model is now a
  // 94.8-native (92.8 world) glazed ARM — two portal facades, a blind-arcaded
  // hall between them, a segmental barrel vault on iron ribs, and a glazed
  // crossing dome over the line — so a racer spends ~2 s under glass (~1 s of
  // approach, ~1 s of rollout at racing pace).
  //
  // THREE MEASURED CONSTRAINTS ARE BAKED INTO THE MESH — read before re-modelling:
  //  1. CLEARANCE is a ratio, not a length. This def scales by WIDTH, so the
  //     model's X extent ALWAYS lands at world ±27 whatever it is natively:
  //     scale = 54 / size.x. Native outer ±27.6 with a ±21.0 clear opening keeps
  //     the shipped ±20.54 world opening against a ±18 road (2.54 u per side).
  //     Lengthening along Z cannot touch it — only editing X can.
  //  2. THE GRANDSTANDS. finishPlaza puts stands at finish offsets −56/−33/+21,
  //     length 17, in the lateral band hw+4.9..hw+17.5 = 22.9..35.5. So the long
  //     side walls stop at 22.9 world and ONLY the two end facades carry the full
  //     ±27 — and they sit at |z| 42.0..46.4 world, which parks the up-course one
  //     in the 6 u gap between the −56 and −33 stand rows. Nothing intersects.
  //  3. THE ROAD ENDS 48 u past the line (12 nodes × step 4) with the end wall at
  //     60, which is what caps the half-length at 46.4 — the exit facade still
  //     lands on asphalt.
  // The gate lintel's underside (native h 12) is the ray that decides how much of
  // the chase frame reads as ceiling: everything above it is glass. Lower it for
  // more glass, raise it for a taller portal.
  galleria: {
    build: (hw) =>
      landmarkModel('models/landmarks/galleria.glb', {
        width: 2 * hw + 18,
        // The hall must read from INSIDE (the finish moment): walls, marble
        // dressing and the glazed vault/dome are single-sided shells —
        // backface culling made them invisible overhead from the chase camera.
        // 'ironwork' joined them: the glazing bars are one-quad ribbons.
        doubleSideMats: ['cream', 'marble', 'glass', 'ironwork'],
        // 0.45 was tuned for the old model's few large panes; on the new blind
        // arcade's 26 mullioned lights that glow rendered as flat orange card in
        // Napoli's noon. 0.16 leaves them as dark glass with a warm centre —
        // which is what a shaded arcade actually looks like at midday.
        ...windowGlass('win', 0.10),
      }),
    placement: 'span',
  },
};
